import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Package, Sunrise, CheckCircle2, Clock, Loader2 } from "lucide-react";

export default function VendedorInventario() {
  const [inventario, setInventario] = useState([]);
  const [turno, setTurno] = useState(null);
  const [recibidas, setRecibidas] = useState({});
  const [notas, setNotas] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [inv, t] = await Promise.all([api.get("/inventario"), api.get("/inventario/mi-turno")]);
      setInventario(inv.data);
      setTurno(t.data);
      if (t.data?.items) {
        setRecibidas(Object.fromEntries(t.data.items.map((i) => [i.producto_id, i.cantidad_recibida ?? i.cantidad_asignada])));
      }
    } catch (error) {
      console.error("Error fetching inventario:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const confirmarApertura = async () => {
    setConfirmando(true);
    try {
      const items = turno.items.map((i) => ({ producto_id: i.producto_id, cantidad_recibida: parseInt(recibidas[i.producto_id]) || 0 }));
      const r = await api.post(`/inventario/turnos/${turno.id}/confirmar`, { items, notas: notas || null });
      if (r.data.estado === "confirmado") toast.success(r.data.message);
      else toast.warning(r.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error confirmando apertura");
    } finally {
      setConfirmando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  const totalDisponible = inventario.reduce((sum, i) => sum + (i.cantidad_disponible || 0), 0);
  const totalAsignado = inventario.reduce((sum, i) => sum + (i.cantidad_asignada || 0), 0);
  const hayDiferencia = turno?.items?.some((i) => (parseInt(recibidas[i.producto_id]) || 0) !== i.cantidad_asignada);

  return (
    <div className="space-y-6" data-testid="vendedor-inventario">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Mi Inventario</h1>
        <p className="text-torta-muted">Stock asignado para venta</p>
      </div>

      {/* Apertura de turno */}
      {turno && !turno.sin_turno && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-torta-brown/20" data-testid="apertura-turno-card">
          <div className="flex items-center gap-2 mb-3">
            <Sunrise className="w-5 h-5 text-torta-brown" />
            <h2 className="font-display text-lg text-torta-dark">Apertura de turno · {turno.fecha}</h2>
          </div>

          {turno.estado === "asignado" && (
            <>
              <p className="text-sm text-torta-muted mb-3">Revisa lo que recibiste físicamente y confirma tu apertura. Si algo no coincide, ajusta la cantidad.</p>
              <div className="space-y-2">
                {turno.items.map((i) => (
                  <div key={i.producto_id} className="flex items-center justify-between gap-3 bg-torta-cream/40 rounded-xl px-3 py-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-torta-dark">{i.producto_nombre}</p>
                      <p className="text-[11px] text-torta-muted">Asignado: {i.cantidad_asignada} und</p>
                    </div>
                    <input
                      type="number" min="0"
                      data-testid={`recibida-${i.producto_id}`}
                      className={`w-20 h-10 text-center rounded-lg border text-sm font-bold ${(parseInt(recibidas[i.producto_id]) || 0) !== i.cantidad_asignada ? "border-amber-400 bg-amber-50 text-amber-800" : "border-torta-sand bg-white text-torta-dark"}`}
                      value={recibidas[i.producto_id] ?? ""}
                      onChange={(e) => setRecibidas({ ...recibidas, [i.producto_id]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              {hayDiferencia && (
                <div className="mt-3">
                  <p className="text-xs text-amber-700 mb-1">⚠️ Reportaste cantidades distintas. Tu apertura quedará pendiente de aprobación del administrador.</p>
                  <input
                    data-testid="notas-apertura"
                    className="w-full px-3 py-2 rounded-lg border border-torta-sand text-sm"
                    placeholder="Cuéntanos qué pasó (opcional)"
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                  />
                </div>
              )}
              <button
                data-testid="confirmar-apertura-btn"
                onClick={confirmarApertura}
                disabled={confirmando}
                className="mt-4 w-full bg-torta-brown hover:bg-torta-brown/90 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {confirmando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {hayDiferencia ? "Enviar con diferencias" : "Confirmar apertura"}
              </button>
            </>
          )}

          {turno.estado === "en_revision" && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center gap-3" data-testid="turno-en-revision">
              <Clock className="w-6 h-6 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Esperando aprobación del administrador</p>
                <p className="text-xs text-amber-700">Reportaste diferencias en tu inventario. Te avisaremos cuando sea aprobado.</p>
              </div>
            </div>
          )}

          {["confirmado", "aprobado"].includes(turno.estado) && (
            <div className="bg-green-50 border border-green-300 rounded-xl p-4 flex items-center gap-3" data-testid="turno-abierto">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="text-sm font-semibold text-green-800">¡Turno abierto! Tu inventario está cargado.</p>
                <p className="text-xs text-green-700">{turno.items.reduce((s, i) => s + (i.cantidad_aprobada ?? i.cantidad_recibida ?? i.cantidad_asignada), 0)} unidades cargadas</p>
              </div>
            </div>
          )}

          {turno.estado === "rechazado" && (
            <div className="bg-red-50 border border-red-300 rounded-xl p-4">
              <p className="text-sm font-semibold text-red-800">Tu apertura fue rechazada. Habla con tu administrador.</p>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <p className="text-torta-muted text-sm mb-1">Total Asignado</p>
          <p className="font-display text-2xl text-torta-dark">{totalAsignado} und</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <p className="text-torta-muted text-sm mb-1">Disponible</p>
          <p className="font-display text-2xl text-green-600">{totalDisponible} und</p>
        </div>
      </div>

      {/* Inventory List */}
      <div className="space-y-3">
        {inventario.map((item) => {
          const vendido = item.cantidad_asignada - item.cantidad_disponible;
          const porcentaje = item.cantidad_asignada > 0
            ? Math.round((item.cantidad_disponible / item.cantidad_asignada) * 100)
            : 0;

          return (
            <div key={item.id} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-torta-cream rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-torta-brown" />
                  </div>
                  <div>
                    <p className="font-medium text-torta-dark">{item.producto_nombre}</p>
                    <p className="text-sm text-torta-muted">Vendidas: {vendido}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl text-torta-dark">{item.cantidad_disponible}</p>
                  <p className="text-xs text-torta-muted">de {item.cantidad_asignada}</p>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${porcentaje > 50 ? "bg-green-500" : porcentaje > 20 ? "bg-amber-400" : "bg-red-500"}`}
                  style={{ width: `${porcentaje}%` }}
                />
              </div>
            </div>
          );
        })}
        {inventario.length === 0 && (
          <p className="text-center py-8 text-torta-muted bg-white rounded-2xl">Aún no tienes inventario cargado. Confirma tu apertura de turno.</p>
        )}
      </div>
    </div>
  );
}
